/**
 * 五阶金字塔排位系统（最新规则）
 *
 * 规则：
 *  - 5 阶：一等 / 二等 / 三等 / 四等 / 五等
 *  - 每阶人数上限：1 / 2 / 3 / 4 / 5（共 15 个虚位）
 *  - 排位：按 fortune.total（综合分）**降序**整体排序
 *      - 分数高者先进一等（1 人）
 *      - 接下来 2 人进二等
 *      - 接下来 3 人进三等
 *      - 接下来 4 人进四等
 *      - 剩下的人（包括同分者）全部进五等（5 人）
 *  - 平局：先到者优先（ts 升序靠前者排在前面）
 *  - **新规则**：如果五等已满（5 人），新名字的分数若能排进 5 等，
 *      会从五等**末尾**挤出分数最低的人（自然顺延到下一位置），
 *      即"虚位"始终被填满，不会留空。
 *  - **第五等保底**：总人数 >= 10 时，第五等至少要有 1 人（实际不会为 0，
 *      因为前 4 阶最多容纳 1+2+3+4=10 人，超 10 必然填到五等）
 *
 *  注：
 *  - 没有"evict"——所有入园的人都在金字塔中显示
 *  - zoo 容量提升到 15（与金字塔虚位对齐）
 */

import type { Element } from "../shared/types";
import { elementOfBody } from "./elements";
import type { ZooEntry } from "./zoo";

/** 阶级数字（1=最高，5=最低） */
export type RankLevel = 1 | 2 | 3 | 4 | 5;

/** 每阶上限（1/2/3/4/5） */
export const RANK_CAPS: Record<RankLevel, number> = {
  1: 1,
  2: 2,
  3: 3,
  4: 4,
  5: 5,
};

/** 阶级中文标签 */
export const RANK_LABELS: Record<RankLevel, string> = {
  1: "一等",
  2: "二等",
  3: "三等",
  4: "四等",
  5: "五等",
};

export interface RankedEntry {
  scpId: string;
  name: string;
  element: Element;
  total: number;
  rank: RankLevel;
  ts: number;
  /** 命中的名人信息（用于 screen4 显示 buff 小标志） */
  fameHit?: boolean;
  fameLabel?: string;
  fameScoreBonus?: number;
  fameTier?: "world" | "continent" | "east-asia" | "nation" | "regional" | null;
}

/**
 * 交换两个条目在金字塔中的 rank（用于对波 PK 胜出后换位）
 *  - 同时互换 total / fameScoreBonus 等字段（保持一致性）
 *  - 不修改 scpId / name / ts
 */
export function swapRankedEntries(a: RankedEntry, b: RankedEntry): void {
  const tmpRank = a.rank;
  const tmpTotal = a.total;
  a.rank = b.rank;
  b.rank = tmpRank;
  a.total = b.total;
  b.total = tmpTotal;
}

/** 入园条目 → 派生元素 */
export function getElement(entry: ZooEntry): Element {
  const monster = entry.monster;
  if (monster.element) return monster.element;
  return elementOfBody(monster.class ?? "");
}

/** 把 ZooEntry 转成 RankedEntry（rank 暂时为 5 占位）
 *  total = fortune 总分 + fame 加成
 *  这样名人 +10~40 分会自动把对应人偶推到更高阶位
 */
function toRanked(e: ZooEntry): RankedEntry {
  const fame = e.fame;
  const baseTotal = e.fortune?.total ?? 50;
  const fameBonus = fame?.hit ? (fame.scoreBonus ?? 0) : 0;
  const total = Math.min(100, baseTotal + fameBonus);
  return {
    scpId: e.scpId,
    name: e.originalName || "无名",
    element: getElement(e),
    total,
    rank: 5,
    ts: e.ts,
    fameHit: fame?.hit ?? false,
    fameLabel: fame?.displayName ?? "",
    fameScoreBonus: fameBonus,
    fameTier: fame?.tier ?? null,
  };
}

export interface RankingResult {
  tiers: Record<RankLevel, RankedEntry[]>;
  /** 完全被挤出占位的条目（理论上不再产生，留作兼容） */
  evicted: RankedEntry[];
  /** 总入园人数（去重后） */
  total: number;
}

/**
 * 应用"分数排序 + 平局先到优先 + 虚位顺延"排位
 *
 * 步骤：
 *  1. 转 RankedEntry
 *  2. 同 originalName 去重（保留最早入园的）
 *  3. 按 total 降序、ts 升序排序
 *  4. 依次填入一等→二等→三等→四等→五等
 *  5. 如果超 15 人（1+2+3+4+5），从五等**末尾**挤出分数最低的人
 *      → 实际上配合 zoo MAX=15，永远不会触发"挤人"
 *  6. 排名规则：高分数在前；分数相同的，ts 早的在前
 *      → 分数高者"插队"在前，低分者向后顺延（自然填空位）
 *
 *  保证：
 *  - 当总人数 <= 15：每阶按 1/2/3/4/5 填入，虚位按剩余补全
 *  - 当总人数 > 10：第五等至少 1 人（因为前 4 阶只容纳 10 人）
 *  - 当总人数 = 15：金字塔满员，无虚位
 */
export function applyRanking(entries: ZooEntry[]): RankingResult {
  // 1. 转 RankedEntry
  const all: RankedEntry[] = entries.map(toRanked);

  // 2. 同 originalName 去重（保留最早入园的）
  const dedup = new Map<string, RankedEntry>();
  for (const e of all) {
    const key = e.name.trim();
    const exist = dedup.get(key);
    if (!exist) {
      dedup.set(key, e);
    } else {
      // 保留更早入园的（ts 较小）
      if (e.ts < exist.ts) dedup.set(key, e);
    }
  }
  const unique = Array.from(dedup.values());

  // 3. 按 total 降序、ts 升序排序
  //    （分数高者在前面 → 后续填入会自然顺延到正确的阶层）
  unique.sort((a, b) => {
    if (a.total !== b.total) return b.total - a.total;
    return a.ts - b.ts;
  });

  // 4. 依次填入 1→2→3→4→5
  //    如果当前阶满员，进入下一阶
  //    如果五等也满员（5 人），则保留前 15 名（不会产生 evict）
  const tiers: Record<RankLevel, RankedEntry[]> = { 1: [], 2: [], 3: [], 4: [], 5: [] };

  const placed = unique.slice(0, 15); // 最多 15 人
  const evicted = unique.slice(15);   // 多余的（实际不会出现，zoo MAX=15）

  for (const e of placed) {
    for (const r of [1, 2, 3, 4, 5] as RankLevel[]) {
      if (tiers[r].length < RANK_CAPS[r]) {
        e.rank = r;
        tiers[r].push(e);
        break;
      }
    }
  }

  return { tiers, evicted, total: unique.length };
}